import { useMemo } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { ApacCheckoutComponent, useApacCheckoutStyles } from "@apac/checkout";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@apac/components/ui";
import {
  type TextFieldProps,
  type DateFieldProps,
  SelectField,
  TextField,
  type SelectFieldProps,
} from "@apac/form-ui";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Gender } from "@hopper-b2b/types";
import { BirthDateField } from "@tenant/apac-travelers";
import { NotificationBanner } from "@tenant/ui";
import { useCountryOptions } from "../../hooks";
import type { NameFieldsType } from "../TravelerForm";
import defaultStyles from "./styles.module.scss";

export type TravelerDetailsFieldOverrides = {
  firstName?: Partial<TextFieldProps<FieldValues>>;
  middleName?: Partial<TextFieldProps<FieldValues>>;
  lastName?: Partial<TextFieldProps<FieldValues>>;
  dateOfBirth?: Partial<DateFieldProps>;
  gender?: Partial<SelectFieldProps>;
  nationality?: Partial<SelectFieldProps>;
};

export interface TravelerDetailsProps {
  formContext: UseFormReturn<FieldValues, unknown, undefined>;
  fieldOverrides: TravelerDetailsFieldOverrides;
  subtitle?: string;
  showGender?: boolean;
  showNationality?: boolean;
  missingInfoError?: boolean;
  nameFieldsOrder?: NameFieldsType[];
}

export const TravelerDetails = ({
  formContext,
  fieldOverrides: {
    firstName = {},
    middleName = {},
    lastName = {},
    dateOfBirth = {},
    gender = {},
    nationality = {},
  },
  subtitle,
  showGender,
  showNationality,
  missingInfoError,
  nameFieldsOrder = ["firstName", "middleName", "lastName"],
}: TravelerDetailsProps) => {
  const { t } = useI18nContext();
  const styles = useApacCheckoutStyles(
    ApacCheckoutComponent.TravellerDetails,
    defaultStyles
  );
  const [, cn] = useModuleBEM(styles, ApacCheckoutComponent.TravellerDetails);

  const genderOptions = useMemo(
    () => [
      { label: t("person_male"), value: Gender.M },
      { label: t("person_female"), value: Gender.F },
    ],
    [t]
  );
  const countryOptions = useCountryOptions();

  const firstNameField = useMemo(
    () => (
      <TextField
        key="firstName"
        className={cn("first-name")}
        formContext={formContext}
        name="firstName"
        label={t("firstName")}
        placeholder={t("firstName")}
        {...firstName}
      />
    ),
    [formContext, cn, t, firstName]
  );

  const middleNameField = useMemo(
    () => (
      <TextField
        key="middleName"
        className={cn("middle-name")}
        formContext={formContext}
        name="middleName"
        label={t("middleNameOptional")}
        placeholder={t("middleName")}
        {...middleName}
      />
    ),
    [formContext, cn, t, middleName]
  );

  const lastNameField = useMemo(
    () => (
      <TextField
        key="lastName"
        className={cn("last-name")}
        formContext={formContext}
        name="lastName"
        label={t("lastName")}
        placeholder={t("lastName")}
        {...lastName}
      />
    ),
    [formContext, cn, t, lastName]
  );

  const nameFieldsMap: Record<NameFieldsType, JSX.Element> = useMemo(
    () => ({
      firstName: firstNameField,
      middleName: middleNameField,
      lastName: lastNameField,
    }),
    [firstNameField, middleNameField, lastNameField]
  );

  return (
    <Accordion defaultValue="traveler-details">
      <AccordionItem value="traveler-details">
        {missingInfoError && (
          <NotificationBanner
            className={cn("message-banner")}
            iconName={IconNameEnum.warningCircle}
            title={t("apac.form.requiredInfoMessage")}
          />
        )}
        <AccordionTrigger className={cn("trigger")}>
          <div className={cn("title")}>{t("travelerForm.title")}</div>
        </AccordionTrigger>

        <AccordionContent>
          <div className={cn("subtitle")}>
            {subtitle ?? t("apac.travelerForm.subtitle")}
          </div>
          <div className={cn("container")}>
            {nameFieldsOrder.map((field) => nameFieldsMap[field])}

            <BirthDateField
              className={cn("date-of-birth")}
              formContext={formContext}
              name="dateOfBirth"
              label={t("dateOfBirth")}
              {...dateOfBirth}
            />

            {showGender && (
              <SelectField
                className={cn("gender")}
                formContext={formContext}
                name="gender"
                label={t("gender")}
                options={genderOptions}
                placeholder={t("select")}
                {...gender}
              />
            )}

            {showNationality && (
              <SelectField
                formContext={formContext}
                name="nationality"
                label={t("nationality")}
                options={countryOptions}
                placeholder={t("select")}
                {...nationality}
              />
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
